/** 班级「行规分」页:每人 基础分 + 加减分 = 总分 → GPA 系数;加减分录入(校宝「点评」)。 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useClassConduct, useConductMutations, type ClassConductRow } from "../../api/gpa";
import { useConductBatch } from "../../api/lessons";
import { useClassWithRoster } from "../../api/school";

export function ClassConductPage(): JSX.Element {
  const { t } = useTranslation();
  const { classId = "" } = useParams();
  const data = useClassConduct(classId);
  const klass = useClassWithRoster(classId);
  const m = useConductMutations();
  const [target, setTarget] = useState<ClassConductRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 批量点评(校宝多人一起点):勾选多人 → 同一条加减分记给每个人
  const batch = useConductBatch();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  if (data.isLoading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (!data.data) return <p className="text-sm text-rose-600">{(data.error as Error | null)?.message ?? t("common.error")}</p>;
  const { config, students } = data.data;
  // 手机底栏的 +1 / −1:记到第一个类别,老师要细分就点「更多」。
  const quick = async (pts: number) => {
    setErr(null); setOkMsg(null);
    try {
      const out = await batch.mutateAsync({ class_id: classId, student_ids: [...picked], category: config.categories[0]?.category ?? t("gpa.conduct.quickCategory"),
        item: null, points: pts, note: null, recorded_on: new Date().toISOString().slice(0, 10) });
      setPicked(new Set()); setOkMsg(t("gpa.conduct.batch.done", { n: out.created }));
    } catch (e) { setErr((e as Error).message); }
  };
  return (
    <div className="pb-28 md:pb-0">
      <Link to={`/classes/${classId}`} className="text-sm text-sky-600 hover:underline">{t("gpa.grades.back")}</Link>
      <header className="mb-4 mt-1">
        <h1 className="text-xl font-semibold text-slate-800 md:text-2xl">{klass.data?.name ?? ""} · {t("gpa.conduct.title")}</h1>
        <p className="text-sm text-slate-500">{t("gpa.conduct.hint", { base: config.base_score })}</p>
      </header>
      {err && <p className="mb-3 text-sm text-rose-600">{err}</p>}
      {okMsg && <p className="mb-3 text-sm text-emerald-700">{okMsg}</p>}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <button type="button" onClick={() => setPicked(picked.size === students.length ? new Set() : new Set(students.map((s) => s.student_id)))} className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
          {picked.size === students.length && students.length > 0 ? t("gpa.conduct.batch.none") : t("gpa.conduct.batch.all")}
        </button>
        <button type="button" disabled={picked.size === 0} onClick={() => { setOkMsg(null); setBatchOpen(true); }} className="rounded bg-sky-500 px-3 py-1 text-xs font-medium text-white hover:bg-sky-600 disabled:opacity-40" data-testid="conduct-batch">
          {t("gpa.conduct.batch.button", { n: picked.size })}
        </button>
        <span className="text-xs text-slate-400">{t("gpa.conduct.batch.hint")}</span>
      </div>
      {/* 手机:点卡片勾选,右侧「±」单独给这个学生记一条 */}
      <ul className="flex flex-col gap-2 md:hidden" data-testid="conduct-cards">
        {students.map((s) => {
          const on = picked.has(s.student_id);
          return (
            <li key={s.student_id} className={`flex items-center gap-3 rounded-lg border bg-white p-3 ${on ? "border-sky-400 bg-sky-50" : "border-slate-200"}`}>
              <button type="button" onClick={() => toggle(s.student_id)} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-pressed={on}>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border text-sm ${on ? "border-sky-500 bg-sky-500 text-white" : "border-slate-300"}`}>{on ? "✓" : ""}</span>
                <span className="min-w-0">
                  <span className="block truncate text-base font-medium text-slate-800">{s.name_zh}</span>
                  <span className="text-xs text-slate-500">
                    {s.plus ? <span className="text-emerald-700">+{s.plus}</span> : null}{s.plus && s.minus ? " " : ""}{s.minus ? <span className="text-rose-600">{s.minus}</span> : null}
                    {s.records ? ` · ${t("gpa.conduct.nRecords", { n: s.records })}` : ""}
                  </span>
                </span>
              </button>
              <span className="text-right">
                <span className="block text-lg font-semibold tabular-nums text-slate-800">{s.total}</span>
                <span className={`text-xs ${s.coefficient >= 1 ? "text-emerald-700" : "text-amber-700"}`}>× {s.coefficient.toFixed(2)}</span>
              </span>
              <button type="button" onClick={() => setTarget(s)} className="h-10 w-10 shrink-0 rounded-lg border border-slate-200 text-lg text-sky-600 active:bg-sky-50" aria-label={t("gpa.conduct.add")}>±</button>
            </li>
          );
        })}
      </ul>

      {picked.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="conduct-mobile-bar">
          <span className="min-w-0 flex-1 text-sm text-slate-700">{t("gpa.conduct.batch.who", { n: picked.size })}</span>
          <button type="button" disabled={batch.isPending} onClick={() => void quick(1)} className="h-10 rounded-lg bg-emerald-600 px-4 text-base font-medium text-white active:bg-emerald-700 disabled:opacity-50">+1</button>
          <button type="button" disabled={batch.isPending} onClick={() => void quick(-1)} className="h-10 rounded-lg bg-rose-600 px-4 text-base font-medium text-white active:bg-rose-700 disabled:opacity-50">−1</button>
          <button type="button" onClick={() => { setOkMsg(null); setBatchOpen(true); }} className="h-10 rounded-lg border border-slate-300 px-3 text-sm text-slate-700">{t("gpa.conduct.more")}</button>
        </div>
      )}

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white md:block">
        <table className="w-full text-sm" data-testid="conduct-grid">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr>
            <th className="w-8 px-3 py-2" />
            <th className="px-3 py-2 text-left">{t("gpa.grades.col.name")}</th><th className="px-3 py-2 text-right">{t("gpa.conduct.plus")}</th><th className="px-3 py-2 text-right">{t("gpa.conduct.minus")}</th>
            <th className="px-3 py-2 text-right">{t("gpa.conduct.total")}</th><th className="px-3 py-2 text-center">{t("gpa.conduct.coefficient")}</th><th className="px-3 py-2 text-left">{t("gpa.conduct.lastOn")}</th><th /></tr></thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.student_id} className={`border-t border-slate-100 ${picked.has(s.student_id) ? "bg-sky-50/60" : ""}`}>
                <td className="px-3 py-1.5"><input type="checkbox" aria-label={s.name_zh} checked={picked.has(s.student_id)} onChange={() => toggle(s.student_id)} /></td>
                <td className="px-3 py-1.5"><Link to={`/students/${s.student_id}`} className="font-medium text-slate-800 hover:underline">{s.name_zh}</Link>{s.name_en && <span className="ml-1 text-xs text-slate-400">{s.name_en}</span>}</td>
                <td className="px-3 py-1.5 text-right text-emerald-700">{s.plus ? `+${s.plus}` : "0"}</td>
                <td className="px-3 py-1.5 text-right text-rose-600">{s.minus || "0"}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{s.total}</td>
                <td className="px-3 py-1.5 text-center"><span className={`rounded px-1.5 py-0.5 ${s.coefficient >= 1 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>× {s.coefficient.toFixed(2)}</span></td>
                <td className="px-3 py-1.5 text-xs text-slate-500">{s.last_on ?? "—"}<span className="ml-1 text-slate-400">({t("gpa.conduct.nRecords", { n: s.records })})</span></td>
                <td className="px-3 py-1.5 text-right"><button type="button" onClick={() => setTarget(s)} className="text-sky-600 hover:underline">{t("gpa.conduct.add")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {target && (
        <ConductModal student={target} categories={config.categories} onClose={() => setTarget(null)}
          onSave={async (r) => { setErr(null); try { await m.add.mutateAsync({ ...r, student_id: target.student_id, class_id: classId }); setTarget(null); } catch (e) { setErr((e as Error).message); } }} />
      )}
      {batchOpen && (
        <ConductModal student={{ name_zh: t("gpa.conduct.batch.who", { n: picked.size }) }} categories={config.categories} onClose={() => setBatchOpen(false)}
          onSave={async (r) => {
            setErr(null);
            try {
              const out = await batch.mutateAsync({ ...r, class_id: classId, student_ids: [...picked] });
              setBatchOpen(false); setPicked(new Set()); setOkMsg(t("gpa.conduct.batch.done", { n: out.created }));
            } catch (e) { setErr((e as Error).message); }
          }} />
      )}
    </div>
  );
}

export function ConductModal({ student, categories, onClose, onSave }: { student: { name_zh: string }; categories: { category: string; items: string[] }[]; onClose: () => void; onSave: (r: { category: string; item: string | null; points: number; note: string | null; recorded_on: string | null }) => void | Promise<void> }) {
  const { t } = useTranslation();
  const [cat, setCat] = useState(categories[0]?.category ?? "");
  const items = categories.find((c) => c.category === cat)?.items ?? [];
  const [item, setItem] = useState("");
  const [points, setPoints] = useState(-1);
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl sm:max-w-md sm:rounded-xl" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()} data-testid="conduct-modal">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">{t("gpa.conduct.modalTitle", { name: student.name_zh })}</h3>
        {/* 一点就选:类别 / 分值(手机上不用打字) */}
        <div className="mb-2 flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button key={c.category} type="button" onClick={() => { setCat(c.category); setItem(""); }}
              className={`rounded-full border px-3 py-1.5 text-sm ${cat === c.category ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600"}`}>{c.category}</button>
          ))}
        </div>
        {items.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {items.map((i) => (
              <button key={i} type="button" onClick={() => setItem(i)}
                className={`rounded-full border px-2.5 py-1 text-xs ${item === i ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500"}`}>{i}</button>
            ))}
          </div>
        )}
        <div className="mb-3 grid grid-cols-6 gap-1.5">
          {[-2, -1, -0.5, 0.5, 1, 2].map((v) => (
            <button key={v} type="button" onClick={() => setPoints(v)}
              className={`h-10 rounded-md border text-sm font-medium tabular-nums ${points === v ? (v > 0 ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-rose-500 bg-rose-50 text-rose-700") : "border-slate-200 text-slate-600"}`}>
              {v > 0 ? `+${v}` : v}
            </button>
          ))}
        </div>
        <div className="grid gap-2 text-xs sm:grid-cols-2">
          <label className="hidden sm:block">{t("gpa.conduct.category")}<select value={cat} onChange={(e) => { setCat(e.target.value); setItem(""); }} className="mt-1 w-full rounded border border-slate-200 px-2 py-1">{categories.map((c) => <option key={c.category}>{c.category}</option>)}</select></label>
          <label>{t("gpa.conduct.item")}<input list="conduct-items" value={item} onChange={(e) => setItem(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /><datalist id="conduct-items">{items.map((i) => <option key={i} value={i} />)}</datalist></label>
          <label className="hidden sm:block">{t("gpa.conduct.points")}<input type="number" step="0.5" value={points} onChange={(e) => setPoints(Number(e.target.value))} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
          <label>{t("gpa.grades.manual.date")}<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
          <label className="sm:col-span-2">{t("gpa.conduct.note")}<input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
        </div>
        <div className="sticky bottom-0 -mx-4 mt-3 flex justify-end gap-2 border-t border-slate-100 bg-white px-4 pt-3 text-sm sm:static sm:mx-0 sm:border-0 sm:px-0 sm:pt-0">
          <button type="button" onClick={onClose} className="rounded border border-slate-300 px-4 py-2 sm:px-3 sm:py-1">{t("common.cancel")}</button>
          <button type="button" disabled={!cat || !points} onClick={() => void onSave({ category: cat, item: item.trim() || null, points, note: note.trim() || null, recorded_on: date || null })} className="rounded bg-sky-500 px-5 py-2 font-medium text-white hover:bg-sky-600 disabled:opacity-50 sm:px-3 sm:py-1">{t("common.save")}</button>
        </div>
      </div>
    </div>
  );
}
